import { inject } from '@angular/core'

import { patchState, signalStore, withMethods, withState } from '@ngrx/signals'

import { Observable, lastValueFrom } from 'rxjs'

import {
  CreateResourceDataSlicesPayload,
  ResourceDataSlice,
  UpdateResourceDataSlicesPayload,
} from '@flowx/designer/modules/core/dictionary/data-mapping.dictionary'
import { ReusableTemplatesApiService } from '@flowx/designer/shared/api/reusable-templates.api'
import { AppResource, mapResource } from '@flowx/designer/shared/entities/application.entity'
import { ReusableTemplate } from '@flowx/designer/shared/entities/reusable-resources.entity'
import { ToastMessageService } from '@flowx/designer/shared/services/toast-message.service'
import {
  SliceApiService,
  withSlices,
} from '@flowx/designer/shared/store/features/slice-management.feature'

export interface ReusableUiTemplateState {
  reusableUiTemplate: ReusableTemplate
  loading: boolean
}

export const initialState: ReusableUiTemplateState = {
  reusableUiTemplate: null,
  loading: false,
}

export const createReusableUiTemplateSliceAdapter = (): SliceApiService => {
  const apiService = inject(ReusableTemplatesApiService)
  return {
    getSlices: (resourceId: string): Observable<AppResource<ResourceDataSlice[]>> =>
      apiService.getDataSlices(resourceId),

    createSlice: (
      resourceId: string,
      slice: CreateResourceDataSlicesPayload
    ): Observable<AppResource<ResourceDataSlice>> => apiService.createDataSlice(resourceId, slice),

    updateSlice: (
      resourceId: string,
      slice: UpdateResourceDataSlicesPayload
    ): Observable<AppResource<ResourceDataSlice>> => apiService.updateDataSlice(resourceId, slice),
  }
}

export const ReusableUiTemplateStore = signalStore(
  withState(initialState),
  withSlices({
    getIdentifier: (state) => state.reusableUiTemplate()?.resourceId,
    createApiService: () => createReusableUiTemplateSliceAdapter(),
  }),
  withMethods(
    (
      store,
      reusableTemplatesApiService = inject(ReusableTemplatesApiService),
      toastMessageService = inject(ToastMessageService)
    ) => ({
      getReusableUiTemplate: async (resourceId: ReusableTemplate['resourceId']) => {
        patchState(store, { loading: true })
        try {
          const reusableUiTemplate = mapResource(
            await lastValueFrom(reusableTemplatesApiService.getReusableTemplateById(resourceId))
          )

          patchState(store, {
            reusableUiTemplate,
            loading: false,
          })
          return reusableUiTemplate
        } catch (err) {
          toastMessageService.showErrorMessage(err?.error?.title ?? 'Error fetching UI template!')
          patchState(store, { loading: false })
        }
      },

      setDescription: (description: ReusableTemplate['description']) => {
        patchState(store, { reusableUiTemplate: { ...store.reusableUiTemplate(), description } })
      },
    })
  )
)
